import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SdgIcon from './SdgIcon';
import { purposeLabel, purposeShort } from './EventTags';
import { apiErrorText, apiRequest } from '../../utils';
import './CommsStats.css';

/* What a theme, a value or a UN goal has actually come to.

   ★ ONE component for three screens. Themes, values and goals are written down in three different
   places and read back through the same two link tables — an event that points at them, and a
   cheer that names them — so the question "is this something we only wrote down, or something we
   live?" has one shape and deserves one answer. Three components would be three copies of it,
   drifting apart the first time one of them grew a column.

   ★ ALL-TIME, with no period tabs. A value declared two years ago is not stale, and a window on a
   screen about what a company stands for would only ever hide part of what it stands for.

   ★ The row a company should act on is the one at ZERO, so every declared item is listed whether
   it has anything to its name or not, and the ones with nothing are SAID to have nothing rather
   than left to look like a rendering gap.

   ⚠️ For a company without the engagement product the payload leaves the event figures OUT
   rather than sending zeros — it cannot run events here, so it has not answered that question —
   and this draws whatever it is given. Do not substitute a 0 for a missing key. */

const Tile = ({ n, label }) => (
  <div className="cst-tile">
    <span className="cst-tile__n">{n}</span>
    <span className="cst-tile__label">{label}</span>
  </div>
);

const PurposeStats = ({ kind }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    apiRequest('GET', `/company/purpose/stats?kind=${kind}`)
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [kind, t]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="bravos-cal__notice">{error}</p>;
  if (data === null) return <span className="auth__spinner" />;

  const rows = data.rows || [];
  // Only where the product is held: see the note above — a missing key is not a zero.
  const hasEvents = Object.prototype.hasOwnProperty.call(data, 'events');

  return (
    <div className="cst">
      <div className="cst-tiles">
        <Tile n={data.declared} label={t(`statsDeclared_${kind}`)} />
        {hasEvents && <Tile n={data.used} label={t('statsOnEvents')} />}
        {kind === 'sdg' && <Tile n={data.with_reason} label={t('statsWithReason')} />}
        <Tile n={data.cheers} label={t('statsCheersNaming')} />
        {/* ★ An AGGREGATE over the union of those events, which is why it is allowed here while
            the rows below still carry no yes-column: an event serving three values would put
            its number in all three ROWS and the column would add up to nothing, whereas an
            event counted once in a set is counted once however many purposes it serves. */}
        {Object.prototype.hasOwnProperty.call(data, 'going') && (
          <Tile n={data.going} label={t('statsAnsweredYes')} />
        )}
      </div>

      {/* The two sentences that say what the numbers are NOT, so neither is read as more than it
          is: a cheer names a value without an event ever having served it, and an event can serve
          three values at once — which is why there is no per-row "answered yes" column here. */}
      <p className="ce-note">{t('statsPurposeNote')}</p>

      {rows.length === 0 ? (
        <p className="ce-note mt-3">{t(`statsNothingDeclared_${kind}`)}</p>
      ) : (
        <div className="cst-breakdown mt-2">
          {rows.map((row) => {
            const name = kind === 'sdg'
              ? purposeShort(purposeLabel(t, { ...row, kind }))
              : row.name;
            const idle = !row.cheers && !(row.events || 0);
            return (
              <div key={`${kind}-${row.id}`} className="cst-line">
                <span className="cst-line__label">
                  <SdgIcon number={row.number} className="etag__sdg" />
                  {name}
                </span>
                <span className="cst-line__sub">
                  {idle
                    ? t('statsNeverUsedYet')
                    : [hasEvents ? t('statsNEvents', { count: row.events || 0 }) : null,
                       t('statsNCheers', { count: row.cheers })].filter(Boolean).join(' · ')}
                </span>
                {/* The number on the right is the one that says whether PEOPLE picked it up.
                    An event is something the company decided; a cheer is somebody choosing this
                    value to describe a colleague, which is the harder and better signal. */}
                <span className="cst-line__n">{row.cheers}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PurposeStats;
